import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { makeObservable, action, computed, observable } from "mobx";
import { fromPromise } from "mobx-utils";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import type { SearchOrgsItemsType, SearchOrgsResponse } from "@whatslly/types/analytics/SearchOrgs";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import type { SearchOrgResponse } from "@whatslly/types/analytics/SearchOrg";
import { FiltersStore } from "./filtersStore";
import type { TuvisInviteUsersResponseType } from "@whatslly/types/TuvisInviteUsersResponse";
import { modalService } from "@whatslly/ui-components/modals";
import { CreateNewOrgModal } from "../modals/CreateNewOrg/CreateNewOrg";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import type { ChangeOrganizationNameResponseType, ChangeOrganizationNameBodyType } from "@whatslly/types/ChageOrganizationName";
import i18n from "../i18n/config";
import type { SFUserSchema, SFUsersList } from "@whatslly/types/analytics/AnalyticsSFTypes";
import { IPromiseBasedObservable } from "mobx-utils/lib/from-promise";
import { isValidSalesforceOrgId } from "@whatslly/commons/utils";
import { AppIds } from "@whatslly/types/LoginProviderType";
import type { ChangeOrganizationIdentityPayload, IOrganizationStore } from "./IOrganizationStore";
const { t } = i18n;

@injectable()
export class OrganizationStore implements IOrganizationStore {
  constructor(
    @inject(InjectUserStore) private userStore: IUserStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<OrganizationStore, "_orgWithNewNameProps">(this, {
      _orgWithNewNameProps: observable,
      searchOrgs: action.bound,
      selectedOrg: computed,
      orgId: computed,
      wPod: computed,
      isViewingOtherOrg: computed,
      isInternalProd: computed,
      primaryLoginProvider: computed,
      isSameOrganization: computed,
      isCrmOrg: computed,
      usersList: computed({ keepAlive: true }),
    });
  }

  async searchOrgs(value: string) {
    const urlParams = new URLSearchParams({
      searchTerm: value,
    });

    const res = await getJSON({
      urlSuffix: `/integrations/api/orgs?${urlParams}`,
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
    }).then((r) => parseOrReturnError<SearchOrgsResponse>(r, this.logger));

    if (!res.success) {
      return { options: [] };
    }

    return {
      options: res.orgs?.map((org) => ({ label: org.Name, value: org.Id, description: org.Description })) || [],
    };
  }

  /**
   * This variable is used to replace the org name in the select dropdown before refreshing the page or sending a request to the server
   */
  private _orgWithNewNameProps: { label: string; orgId: string; description?: string } | undefined;

  get selectedOrg() {
    if (this._orgWithNewNameProps?.label === "") {
      // force update
    }
    return fromPromise(
      this.getOrgById(this.filtersStore.orgId).then((res) => {
        if (!this._orgWithNewNameProps) return res;
        if (res?.value === this._orgWithNewNameProps.orgId) return { ...res, label: this._orgWithNewNameProps.label };
        return res;
      })
    );
  }

  get orgId(): IPromiseBasedObservable<string> {
    if (!this.userStore.initialized) return fromPromise(new Promise<string>((resolve) => resolve("")));
    return fromPromise(this.selectedOrg.then((org) => org?.value ?? this.userStore.user?.organizationId ?? ""));
  }

  get isInternalProd() {
    return this.orgId.value === "00D4J0000004CGZUA2";
  }

  get isSameOrganization() {
    if (!this.userStore.initialized || !this.selectedOrg.value || !this.filtersStore.orgId) return true;
    return Boolean(this.orgId.state === "fulfilled" && this.userStore.user?.organizationId === this.orgId.value);
  }

  get isAccountOrg() {
    if (this.selectedOrg.state === "fulfilled" && this.selectedOrg.value?.accountId && !this.userStore?.isInternalUser) {
      const orgAccountId = this.selectedOrg.value?.accountId;
      return (this.userStore.user?.accounts ?? []).some((account) => account.accountId === orgAccountId);
    }

    return false;
  }

  get wPod() {
    return fromPromise(this.selectedOrg.then((org) => org?.wPod ?? this.userStore.pod));
  }

  get isViewingOtherOrg() {
    return Boolean(
      (this.userStore.hasAccountAdmin || this.userStore.isInternalUser) &&
        this.filtersStore.orgId &&
        this.filtersStore.orgId !== this.userStore.user?.organizationId
    );
  }

  get primaryLoginProvider() {
    if (!this.isViewingOtherOrg) {
      return this.userStore.loginProvider;
    }
    if (isValidSalesforceOrgId(this.filtersStore.orgId)) {
      return AppIds.SALESFORCE;
    }
    return AppIds.MICROSOFT_DYNAMICS;
  }

  get isCrmOrg() {
    return this.usersList.case({
      fulfilled: (users) => {
        return users.some((user) =>
          [AppIds.SALESFORCE, AppIds.MICROSOFT_DYNAMICS].includes((user.loginProvider ?? "") as "Salesforce" | "Microsoft Dynamics")
        );
      },
    });
  }

  async getOrgById(orgId: string | undefined) {
    if (!orgId || !this.userStore.hasAccessToMultipleOrgs) return null;
    const urlParams = new URLSearchParams({ orgId });
    const res = await getJSON({
      urlSuffix: `/integrations/api/org?${urlParams}`,
      etoken: this.userStore.etoken ?? "",
      pod: this.userStore.pod,
      ignoreInterceptResponse: true,
    })
      .then((r) => parseOrReturnError<SearchOrgResponse>(r, this.logger))
      .catch((err) => {
        if (err.status === 401 && this.filtersStore.orgId !== this.userStore.user?.organizationId) {
          this.filtersStore.orgId = "";
          return { success: false, org: null };
        }
        throw err;
      });

    if (!res.success || !res.org) {
      throw new Error("Failed to get org");
    }
    return {
      label: res.org.Name,
      value: res.org.Id,
      accountId: res.org.AccountId,
      wPod: res.org.wPod,
      description: res.org.Description,
      orgType: res.org.OrgType,
      externalLinks: res.org.externalLinks,
    };
  }

  async changeOrgName(payload: ChangeOrganizationIdentityPayload) {
    if (!this.userStore.isAnyAdmin) return;
    const body: ChangeOrganizationNameBodyType = {
      newName: payload.newName,
      orgId: this.filtersStore.orgId,
      description: payload.description,
      orgType: payload.orgType,
      isActive: payload.isActive,
      isPOC: payload.isPOC,
      POCExpiration: payload.POCExpiration,
      org_partner: payload.org_partner as ChangeOrganizationNameBodyType["org_partner"],
      defaultLanguage: payload.defaultLanguage as ChangeOrganizationNameBodyType["defaultLanguage"],
      serverlocation: payload.serverlocation as ChangeOrganizationNameBodyType["serverlocation"],
      available_crm: payload.available_crm as ChangeOrganizationNameBodyType["available_crm"],
    };

    const res = await putJSON({
      urlSuffix: "/integrations/organizations/name",
      etoken: this.userStore.etoken ?? "",
      pod: this.userStore.pod,
      body: JSON.stringify(body),
    }).then((r) => parseOrReturnError<ChangeOrganizationNameResponseType>(r, this.logger));

    if (res.success) {
      this._orgWithNewNameProps = { label: payload.newName, orgId: this.filtersStore.orgId, description: payload.description };
      modalService.openModal(Toast, { text: t("search_orgs.organization_name_updated"), type: "regular" });
      return true;
    } else {
      modalService.openModal(Toast, { text: t("search_orgs.failed_to_update_organization_name"), type: "error" });
    }
  }

  async createNewOrg(orgAccountId?: string) {
    if (!this.userStore.isAnyAdmin) {
      return;
    }
    const result = await modalService.openModal(CreateNewOrgModal).promise;
    if (!result) {
      return;
    }
    const response = await postJSON({
      urlSuffix: "/integrations/users/invites/new-org",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD?.toString() || "us1",
      body: JSON.stringify({
        inviteToPlatform: true,
        companyName: result.name,
        accountId: result.accountId,
        orgAccountId,
        serverLocation: result.serverLocation,
        orgLanguage: result.orgLanguage,
        orgType: result.orgType,
        comment: result.comment,
        data: result.data ?? [],
        crmOrgId: result.crmOrgId,
        crmFieldValue: result.crmFieldValue,
        crmAvailable: result.crmAvailable,
      }),
    }).then((r) => parseOrReturnError<TuvisInviteUsersResponseType>(r, this.logger));
    if (!response.success) {
      modalService.openModal(Toast, { text: response.error, type: "error" });
      return;
    }
    if (!response.orgId) {
      return;
    }
    this.filtersStore.orgId = response.orgId;
    modalService.openModal(Toast, {
      text: `Empty org created: ${response.orgId}`,
      type: "regular",
    });

    return true;
  }

  get usersList(): IPromiseBasedObservable<(SFUserSchema & { userId: string })[]> {
    if (!this.userStore.initialized || !this.userStore.user) {
      return fromPromise(new Promise(() => {}));
    }
    return fromPromise(
      this.orgId.then((orgId) =>
        getJSON({
          urlSuffix: `/analytics/query/users_list?orgId=${orgId}`,
          etoken: this.userStore.etoken ?? "",
          pod: import.meta.env.VITE_API_DEFAULT_ANALYTICS_POD,
        })
          .then((r) => parseOrReturnError<SFUsersList>(r, this.logger))
          .then((r) => Object.entries(r).map(([userId, user]) => ({ userId, ...user })))
          .catch(() => [])
      )
    );
  }
}
